import { inject, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { OfferingLinkMap } from '../util/offering-mention.util';

/**
 * Renders a safe subset of Markdown to HTML for the assistant chat transcript.
 *
 * The assistant streams Markdown (headings, lists, bold, inline/fenced code,
 * links, blockquotes, tables). Rendering it as plain text is hard to read, so
 * this pipe converts it to a small, fixed set of HTML elements.
 *
 * Security model: every piece of text derived from the input is HTML-escaped
 * BEFORE any markup is generated, and only a hard-coded set of tags is emitted.
 * Link URLs are scheme-validated (http/https/mailto/relative only) so a
 * `javascript:` URL can never become an anchor. Because the output is fully
 * controlled and escaped, it is wrapped with `bypassSecurityTrustHtml` to keep
 * the generated markup intact.
 *
 * The optional second argument turns offering names into links to their pages.
 * It belongs here rather than in a pass over the finished HTML precisely
 * because of that security model: the whitelist is the only thing between model
 * output and the DOM, and a second pass over `bypassSecurityTrustHtml`-ed
 * markup would be a second place to get escaping wrong. Inside, the names are
 * matched against text that is already escaped and already has code spans and
 * anchors parked out of the way.
 */
@Pipe({
  name: 'markdown',
})
export class MarkdownPipe implements PipeTransform {
  private readonly sanitizer = inject(DomSanitizer);

  transform(
    value: string | null | undefined,
    offeringLinks?: OfferingLinkMap | null,
  ): SafeHtml {
    if (!value) {
      return '';
    }
    return this.sanitizer.bypassSecurityTrustHtml(render(value, offeringLinks));
  }
}

// Marker used to park extracted spans/blocks so later passes don't touch them.
// A NUL byte cannot appear in escaped HTML or user text, so collisions are
// impossible. Written as a \u0000 escape so the source stays plain ASCII.
const MARK = '\u0000';

// Deepest HTML heading level the browser understands. Model output starting
// with `#` is demoted so it does not collide with the page's own h1/h2.
const MAX_HEADING_LEVEL = 6;

/** Escape the five characters that are meaningful in HTML text/attributes. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Allow only safe link schemes; everything else is treated as plain text. */
function isSafeUrl(url: string): boolean {
  return /^(https?:\/\/|mailto:|\/|#|\.\/|\.\.\/)/i.test(url.trim());
}

/**
 * True for a URL that leaves the SPA.
 *
 * `target="_blank"` used to go on every anchor, which was harmless while every
 * link the model produced was an absolute one. It is not harmless for the
 * offering links below: a relative `/offerings/…` is in-app navigation, and
 * opening it in a new tab would lose the conversation the reader is in.
 *
 * Exported because `assistant-cta.util` draws the same line for a different
 * purpose — only a link that leaves the app can be a call to action — and two
 * statements of "external" that could disagree is one too many.
 */
export function isExternalUrl(url: string): boolean {
  return /^(https?:\/\/|mailto:)/i.test(url.trim());
}

/** A whole-word boundary for offering-name matching: no letter, no digit. */
const NAME_BOUNDARY = '[^\\p{L}\\p{N}]';

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Emphasis only — bold before italic, so `**` is consumed before `*`. */
function applyEmphasis(text: string): string {
  return (
    text
      .replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
      .replace(/__([^_]+?)__/g, '<strong>$1</strong>')
      .replace(/\*([^*\n]+?)\*/g, '<em>$1</em>')
      // Underscore italics only when flanked by non-word chars (skip snake_case).
      .replace(/(^|[^\w])_([^_\n]+?)_(?=[^\w]|$)/g, '$1<em>$2</em>')
  );
}

/** Build one anchor. `url` and `label` must already be HTML-escaped. */
function anchor(url: string, label: string, extraAttrs = ''): string {
  const outward = isExternalUrl(url)
    ? ' target="_blank" rel="noopener noreferrer"'
    : '';
  return `<a href="${url}"${extraAttrs}${outward}>${label}</a>`;
}

/**
 * Turn offering names in `text` into links to their pages.
 *
 * `text` is escaped, with code spans and existing anchors already parked behind
 * placeholders — so a name inside a code span or inside a `[label](url)` link
 * is invisible here and cannot be linked twice.
 *
 * The produced anchors are parked too, which is what makes the longest-first
 * ordering of the map work: once "Power BI Premium" is a placeholder, the
 * shorter "Power BI" can no longer claim its first two words.
 *
 * `data-offering-slug` is the hook the transcript's delegated click handler
 * looks for. Anchors injected through `innerHTML` get no Angular bindings, so
 * `routerLink` is not available here and the href has to carry the truth.
 */
function linkifyOfferings(
  text: string,
  links: OfferingLinkMap,
  parked: string[],
): string {
  let out = text;

  for (const [name, slug] of links) {
    const pattern = new RegExp(
      `(^|${NAME_BOUNDARY})(${escapeRegExp(escapeHtml(name))})($|${NAME_BOUNDARY})`,
      'giu',
    );
    // Only the first mention becomes a link. An answer naming one offering
    // eight times would otherwise put eight identical entries in a screen
    // reader's link list, and the ninth adds nothing the first did not.
    let linked = false;
    out = out.replace(pattern, (match, before: string, hit: string, after: string) => {
      // A placeholder's own NUL delimiters would otherwise count as boundaries,
      // so an offering named like one ("B0", "C1") could match inside a parked
      // span and corrupt it.
      if (linked || before === MARK || after === MARK) {
        return match;
      }
      linked = true;
      parked.push(
        anchor(
          `/offerings/${encodeURIComponent(slug)}`,
          hit,
          ` data-offering-slug="${escapeHtml(slug)}"`,
        ),
      );
      return `${before}${MARK}A${parked.length - 1}${MARK}${after}`;
    });
  }

  return out;
}

/**
 * Convert inline Markdown within a single line of already-block-classified
 * text. The input is escaped first, so any raw HTML in the source is inert.
 */
function renderInline(text: string, links?: OfferingLinkMap | null): string {
  let out = escapeHtml(text);

  // Protect inline code spans first so their contents are never re-formatted.
  const codeSpans: string[] = [];
  out = out.replace(/`([^`]+)`/g, (_m, code: string) => {
    codeSpans.push(`<code>${code}</code>`);
    return `${MARK}C${codeSpans.length - 1}${MARK}`;
  });

  // Links: [label](url). The url is already escaped; validate its scheme.
  // Parked like code spans, so offering names inside a label are left alone —
  // the author already said where that text should point. Emphasis is applied
  // to the label on the way in, because the emphasis pass below runs on text
  // the anchors are no longer part of.
  const anchors: string[] = [];
  out = out.replace(
    /\[([^\]]+)\]\(([^)\s]+)\)/g,
    (match, label: string, url: string) => {
      if (!isSafeUrl(url)) {
        return match;
      }
      anchors.push(anchor(url, applyEmphasis(label)));
      return `${MARK}A${anchors.length - 1}${MARK}`;
    },
  );

  if (links?.size) {
    out = linkifyOfferings(out, links, anchors);
  }

  out = applyEmphasis(out);

  // Restore anchors, then the code spans they may not contain.
  out = out.replace(/\u0000A(\d+)\u0000/g, (_m, i: string) => anchors[+i]);
  out = out.replace(/\u0000C(\d+)\u0000/g, (_m, i: string) => codeSpans[+i]);

  return out;
}

/** Split a Markdown table row into trimmed cell strings. */
function splitRow(row: string): string[] {
  return row
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((c) => c.trim());
}

/** A separator row like `| --- | :--: |` marks the line above as a header. */
function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/.test(line);
}

/** Render a full Markdown document to a safe HTML string. */
function render(src: string, links?: OfferingLinkMap | null): string {
  const normalized = src.replace(/\r\n?/g, '\n');

  // Extract fenced code blocks up front and park them behind placeholders.
  const blocks: string[] = [];
  const withoutFences = normalized.replace(
    /```(\w*)\n?([\s\S]*?)```/g,
    (_m, lang: string, code: string) => {
      const cls = lang ? ` class="language-${escapeHtml(lang)}"` : '';
      const body = escapeHtml(code.replace(/\n$/, ''));
      // `tabindex="0"` because the block scrolls horizontally: a fenced line
      // wider than the bubble — a shell command, a long import path — is
      // otherwise unreachable without a pointer (AXE
      // scrollable-region-focusable, WCAG 2.1.1).
      blocks.push(`<pre tabindex="0"><code${cls}>${body}</code></pre>`);
      return `\n${MARK}B${blocks.length - 1}${MARK}\n`;
    },
  );

  const lines = withoutFences.split('\n');
  const html: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line: nothing to emit.
    if (!line.trim()) {
      i++;
      continue;
    }

    // A parked fenced code block sits alone on its line.
    const blockMatch = line.match(/^\u0000B(\d+)\u0000$/);
    if (blockMatch) {
      html.push(blocks[+blockMatch[1]]);
      i++;
      continue;
    }

    // Headings: # .. ######
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      // Demoted by two: this markdown is model output dropped into a page that
      // already has its own h1/h2, and an answer starting with "# " would
      // otherwise inject a competing h1 and break heading order at random.
      const level = Math.min(MAX_HEADING_LEVEL, heading[1].length + 2);
      html.push(`<h${level}>${renderInline(heading[2].trim(), links)}</h${level}>`);
      i++;
      continue;
    }

    // Horizontal rule.
    if (/^(\s*)(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      html.push('<hr />');
      i++;
      continue;
    }

    // Blockquote: one or more consecutive `>` lines.
    if (/^\s*>\s?/.test(line)) {
      const quote: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^\s*>\s?/, ''));
        i++;
      }
      html.push(`<blockquote>${renderInline(quote.join(' '), links)}</blockquote>`);
      continue;
    }

    // Table: a `|` row immediately followed by a separator row.
    if (
      line.includes('|') &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      const headerCells = splitRow(line);
      i += 2; // skip header + separator
      const bodyRows: string[][] = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        bodyRows.push(splitRow(lines[i]));
        i++;
      }
      const head = headerCells
        .map((c) => `<th scope="col">${renderInline(c, links)}</th>`)
        .join('');
      const body = bodyRows
        .map(
          (row) =>
            `<tr>${row.map((c) => `<td>${renderInline(c, links)}</td>`).join('')}</tr>`,
        )
        .join('');
      // The table is wrapped rather than made a scroll container itself. A
      // `<table>` whose `display` is not `table` stops generating a CSS table
      // box, and Chromium and WebKit then drop the table/row/cell roles from
      // the accessibility tree — a comparison table would be announced as an
      // undifferentiated run of text (WCAG 1.3.1), and no AXE rule reports it.
      // The wrapper takes the overflow, and `tabindex` makes it scrollable by
      // keyboard.
      html.push(
        `<div class="inexp-markdown__scroll" tabindex="0" role="group" aria-label="Table">` +
          `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>` +
          `</div>`,
      );
      continue;
    }

    // Unordered list.
    if (/^\s*[-*+]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(renderInline(lines[i].replace(/^\s*[-*+]\s+/, ''), links));
        i++;
      }
      html.push(`<ul>${items.map((it) => `<li>${it}</li>`).join('')}</ul>`);
      continue;
    }

    // Ordered list.
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(renderInline(lines[i].replace(/^\s*\d+\.\s+/, ''), links));
        i++;
      }
      html.push(`<ol>${items.map((it) => `<li>${it}</li>`).join('')}</ol>`);
      continue;
    }

    // Paragraph: gather consecutive lines until a blank or a block boundary.
    const para: string[] = [];
    while (i < lines.length && lines[i].trim()) {
      const l = lines[i];
      if (
        /^(#{1,6})\s+/.test(l) ||
        /^\s*>\s?/.test(l) ||
        /^\s*[-*+]\s+/.test(l) ||
        /^\s*\d+\.\s+/.test(l) ||
        /^(\s*)(-{3,}|\*{3,}|_{3,})\s*$/.test(l) ||
        /^\u0000B(\d+)\u0000$/.test(l)
      ) {
        break;
      }
      para.push(l);
      i++;
    }
    if (para.length) {
      html.push(`<p>${para.map((l) => renderInline(l, links)).join('<br />')}</p>`);
    }
  }

  return html.join('\n');
}
